import React, { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import RichTextToolbar from './RichTextToolbar';

// Completely disable cursor blinking for focused writing
const cursorStyles = `
  [contenteditable="true"] {
    animation: none !important;
    caret-color: rgba(55, 53, 47, 1) !important;
  }
  
  [data-theme="dark"] [contenteditable="true"] {
    caret-color: rgba(224, 224, 224, 1) !important;
  }
  
  /* Force steady cursor - override all browser defaults */
  [contenteditable="true"]:focus {
    caret-color: rgba(55, 53, 47, 1) !important;
    animation: none !important;
  }
  
  [data-theme="dark"] [contenteditable="true"]:focus {
    caret-color: rgba(224, 224, 224, 1) !important;
    animation: none !important;
  }
  
  /* Industry standard selection colors */
  [contenteditable="true"]::selection {
    background-color: rgba(0, 123, 255, 0.25) !important;
    color: inherit !important;
  }
  
  [contenteditable="true"] *::selection {
    background-color: rgba(0, 123, 255, 0.25) !important;
    color: inherit !important;
  }
  
  [data-theme="dark"] [contenteditable="true"]::selection {
    background-color: rgba(100, 181, 246, 0.3) !important;
    color: inherit !important;
  }
  
  [data-theme="dark"] [contenteditable="true"] *::selection {
    background-color: rgba(100, 181, 246, 0.3) !important;
    color: inherit !important;
  }
  
  /* Override any global selection styles */
  .editor-content::selection,
  .editor-content *::selection {
    background-color: rgba(0, 123, 255, 0.25) !important;
    color: inherit !important;
  }
  
  /* Override system cursor blink */
  * {
    caret-color: inherit !important;
  }
`;

// Clean document container - back to original
const EditorContainer = styled(Box)(({ theme, editorTheme, isDark }) => ({
  maxWidth: '800px', // Quip-style width for collaborative feel
  margin: '0 auto', // Centered like Notion/Medium
  backgroundColor: isDark ? '#2f3437' : 'transparent', // Transparent container, let editor have the white background
  boxShadow: 'none', // Remove shadow for cleaner look like Medium
  position: 'relative',
  // Let theme control height via CSS classes
  // Ensure content stays within bounds
  overflow: 'hidden', // Prevent content from escaping
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  borderRadius: '0', // No border radius like Medium
  border: 'none', // No border like Medium
}));

const EditorContent = styled(Box)(({ theme, editorTheme = {}, height, placeholder, isDark }) => {
  // Centralized color definitions
  const headingColor = isDark ? '#f1f5f9' : '#6b7280';
  const bodyColor = isDark ? '#e2e8f0' : '#6b7280';
  
  return {
  overflow: 'auto',
  padding: '40px 60px', // Reduced top/bottom padding
  outline: 'none',
  color: editorTheme.text || (isDark ? '#e8e8e8' : '#4b5563'), // Softer medium gray
  fontSize: '21px',
  lineHeight: 1.65, // More relaxed line height
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', // Quip font stack
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  backgroundColor: isDark ? '#2f3437' : '#ffffff', // White content on gray background
  position: 'relative',
  borderRadius: '8px', // Subtle, modern curve like Quip
  boxShadow: isDark 
    ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
    : '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)', // Subtle but visible shadow
  
  // Softer cursor styling
  caretColor: isDark ? 'rgba(232, 232, 232, 0.8)' : 'rgba(44, 44, 44, 0.8)',
  cursor: 'text',
  
  '& *': {
    cursor: 'text !important',
  },
  
  // Warmer selection styling
  '& ::selection': {
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 230, 180, 0.5)',
    color: 'inherit'
  },
  
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '6px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: isDark 
      ? 'rgba(255,255,255,0.2)' 
      : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
    '&:hover': {
      background: isDark 
        ? 'rgba(255,255,255,0.3)' 
        : 'rgba(0,0,0,0.3)'
    }
  },
  // Firefox scrollbar
  scrollbarWidth: 'thin',
  scrollbarColor: isDark 
    ? 'rgba(255,255,255,0.2) transparent' 
    : 'rgba(0,0,0,0.2) transparent',
    
  // Notion-like typography styles with Quip font
  '& h1': {
    fontSize: '42px',
    fontWeight: '700',
    lineHeight: '1.04',
    letterSpacing: '-0.015em',
    marginTop: '56px',
    marginBottom: '13px',
    marginLeft: '0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    color: headingColor,
  },
  '& h2': {
    fontSize: '34px',
    fontWeight: '700',
    lineHeight: '1.15',
    letterSpacing: '-0.015em',
    marginTop: '48px',
    marginBottom: '10px',
    marginLeft: '0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    color: headingColor,
  },
  '& h3': {
    fontSize: '26px',
    fontWeight: '700',
    lineHeight: '1.22',
    letterSpacing: '-0.012em',
    marginTop: '40px',
    marginBottom: '8px',
    marginLeft: '0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    color: headingColor,
  },
  '& h4, & h5, & h6': {
    fontSize: '21px',
    fontWeight: '700',
    lineHeight: '1.22',
    marginTop: '32px',
    marginBottom: '8px',
    marginLeft: '0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    color: headingColor,
  },
  '& p': {
    fontSize: '21px',
    lineHeight: '1.58',
    marginTop: '0',
    marginBottom: '29px',
    fontFamily: 'Georgia, "Times New Roman", serif',
    color: bodyColor,
  },
  '& blockquote': {
    fontSize: '21px',
    fontStyle: 'italic',
    lineHeight: '1.58',
    marginLeft: '23px',
    marginRight: '0',
    marginTop: '29px',
    marginBottom: '29px',
    paddingLeft: '23px',
    borderLeft: `3px solid ${isDark ? '#5a5a5a' : '#e6e6e6'}`,
    color: isDark ? '#9b9a97' : '#6b6b6b',
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  
  // Notion-like list styling
  '& ul, & ol': {
    fontSize: '21px',
    lineHeight: '1.58',
    paddingLeft: '30px',
    marginTop: '29px',
    marginBottom: '29px',
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  '& ul ul, & ol ol, & ul ol, & ol ul': {
    marginTop: '8px',
    marginBottom: '8px',
  },
  '& li': {
    marginBottom: '8px',
    color: isDark ? '#e0e0e0' : '#37352f',
  },
  '& ul[style*="list-style-type: circle"]': {
    '& > li': {
      listStyleType: 'circle',
    }
  },
  '& ul[style*="list-style-type: square"]': {
    '& > li': {
      listStyleType: 'square',
    }
  },
  
  // Notion-style code styling
  '& code': {
    fontFamily: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    fontSize: '16px',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(135, 131, 120, 0.15)',
    padding: '2px 6px',
    borderRadius: '3px',
    color: isDark ? '#eb5757' : '#eb5757',
  },
  '& pre': {
    fontFamily: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    fontSize: '16px',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f7f6f3',
    padding: '16px',
    borderRadius: '8px',
    overflow: 'auto',
    marginTop: '29px',
    marginBottom: '29px',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e9e9e7'}`,
    color: isDark ? '#e0e0e0' : '#37352f',
  },
  '&:empty::before': {
    content: `"${placeholder}"`,
    color: isDark ? '#666666' : '#9b9b9b', // Medium-like placeholder color
    opacity: 1,
    fontStyle: 'italic',
    pointerEvents: 'none',
    position: 'absolute',
    fontSize: '21px',
    lineHeight: '1.58',
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  
  // Ensure all text elements stay within bounds
  '& *': {
    maxWidth: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    boxSizing: 'border-box'
  },
  // Style for rich text elements
  '& h1': {
    fontSize: '2.5rem',
    fontWeight: '700',
    margin: '24px 0 16px 0',
    lineHeight: 1.2,
    color: editorTheme.text || '#000000',
    wordWrap: 'break-word'
  },
  '& h2': {
    fontSize: '2rem',
    fontWeight: '600',
    margin: '20px 0 12px 0',
    lineHeight: 1.3,
    color: editorTheme.text || '#000000',
    wordWrap: 'break-word'
  },
  '& h3': {
    fontSize: '1.5rem',
    fontWeight: '600',
    margin: '16px 0 10px 0',
    lineHeight: 1.4,
    color: editorTheme.text || '#000000',
    wordWrap: 'break-word'
  },
  '& h4': {
    fontSize: '1.25rem',
    fontWeight: '500',
    margin: '14px 0 8px 0',
    lineHeight: 1.4,
    color: editorTheme.text || '#000000',
    wordWrap: 'break-word'
  },
  '& h5, & h6': {
    fontSize: '1rem',
    fontWeight: '500',
    margin: '12px 0 6px 0',
    lineHeight: 1.5,
    color: editorTheme.textSecondary || '#666666',
    wordWrap: 'break-word'
  },
  '& p': {
    margin: '4px 0', // Reduced margin for tighter spacing
    wordWrap: 'break-word',
    transition: 'margin-left 0.2s ease' // Smooth transition for indent changes
  },
  '& ul, & ol': {
    margin: '8px 0',
    paddingLeft: '24px'
  },
  '& li': {
    margin: '4px 0',
    wordWrap: 'break-word'
  },
  '& blockquote': {
    margin: '16px 0',
    paddingLeft: '16px',
    borderLeft: `4px solid ${editorTheme.primary || '#007bff'}`,
    fontStyle: 'italic',
    color: editorTheme.textSecondary || '#666666',
    wordWrap: 'break-word'
  },
  '& a': {
    color: editorTheme.primary,
    textDecoration: 'underline',
    wordWrap: 'break-word',
    '&:hover': {
      textDecoration: 'none'
    }
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '4px',
    margin: '8px 0'
  },
  '& code': {
    backgroundColor: `${editorTheme.textSecondary || '#666666'}15`,
    padding: '2px 4px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '0.9em',
    wordWrap: 'break-word'
  },
  '& pre': {
    backgroundColor: `${editorTheme.textSecondary || '#666666'}10`,
    padding: '12px',
    borderRadius: '4px',
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.9em',
    lineHeight: '1.4',
    whiteSpace: 'pre',
    wordWrap: 'normal',
    margin: '8px 0',
    '& *': {
      margin: 0,
      padding: 0,
      lineHeight: 'inherit'
    }
  },
  // Table styling
  '& .table-container': {
    margin: '16px 0',
    overflowX: 'auto',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    maxWidth: '100%'
  },
  '& .editable-table': {
    borderCollapse: 'collapse',
    width: '100%',
    minWidth: '500px',
    border: '2px solid #e0e0e0',
    background: 'white',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  '& .table-cell': {
    border: '1px solid #d0d0d0',
    padding: '12px 16px',
    textAlign: 'left',
    position: 'relative',
    minWidth: '120px',
    minHeight: '40px',
    transition: 'all 0.2s ease',
    wordWrap: 'break-word',
    '&:hover': {
      backgroundColor: '#e3f2fd !important'
    }
  },
  '& .editable-table th': {
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    fontWeight: '600',
    borderBottom: '2px solid #d0d0d0'
  },
  '& .editable-table tbody tr:nth-of-type(even) td': {
    backgroundColor: '#fafafa'
  },
  '& .editable-table tbody tr:nth-of-type(odd) td': {
    backgroundColor: 'white'
  },
  // Legacy table styling (fallback)
  '& table:not(.editable-table)': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '16px 0',
    border: `1px solid ${editorTheme.textSecondary || '#666666'}40`,
    maxWidth: '100%',
    overflowX: 'auto'
  },
  '& th:not(.table-cell), & td:not(.table-cell)': {
    border: `1px solid ${editorTheme.textSecondary || '#666666'}40`,
    padding: '12px',
    textAlign: 'left',
    wordWrap: 'break-word'
  },
  '& th:not(.table-cell)': {
    backgroundColor: `${editorTheme.textSecondary || '#666666'}10`,
    fontWeight: '600'
  },
  '& tr:nth-of-type(even):not(.editable-table tr)': {
    backgroundColor: `${editorTheme.textSecondary || '#666666'}05`
  },
  // Checklist styling
  '& input[type="checkbox"]': {
    marginRight: '8px',
    cursor: 'pointer'
  },
  '& label': {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    margin: '4px 0',
    userSelect: 'none',
    wordWrap: 'break-word'
  }
}});

const StatsContainer = styled(Box)(({ theme, editorTheme = {} }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: '16px 80px', // Match document margins
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${editorTheme.textSecondary || '#666666'}20`,
  backgroundColor: 'transparent',
  borderRadius: '0 0 8px 8px'
}));

const StatChip = styled(Chip)(({ theme, editorTheme = {} }) => ({
  fontSize: '0.75rem',
  height: '24px',
  color: editorTheme.textSecondary || '#666666',
  borderColor: `${editorTheme.textSecondary || '#666666'}30`
}));

const RichTextEditor = forwardRef(({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Untitled Document", 
  theme,
  isDark = false,
  height = '400px',
  showAnalysisButtons = false,
  onAnalyze,
  isAnalyzing = false
}, ref) => {
  const editorRef = useRef(null);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [sentenceCount, setSentenceCount] = useState(0);

  // Memoize text statistics calculation
  const updateStats = useCallback(() => {
    if (editorRef.current) {
      const textContent = editorRef.current.textContent || '';
      const words = textContent.split(/\s+/).filter(word => word.length > 0);
      const sentences = textContent.split(/[.!?]+/).filter(s => s.trim().length > 0);
      
      setWordCount(words.length);
      setCharCount(textContent.length);
      setSentenceCount(sentences.length);
    }
  }, []);

  // Auto-focus editor on mount for immediate writing
  useEffect(() => {
    if (editorRef.current) {
      // Focus the editor automatically
      editorRef.current.focus();
      
      // Place cursor at the end of content
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // Collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    getPlainText: () => editorRef.current ? editorRef.current.textContent || '' : '',
    getHtmlContent: () => editorRef.current ? editorRef.current.innerHTML : '',
    setHtmlContent: (html) => {
      if (editorRef.current) {
        editorRef.current.innerHTML = html;
        handleInput();
      }
    },
    focus: () => editorRef.current?.focus()
  }), []);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
      updateStats();
    }
  }, [value, updateStats]);

  // Auto-focus editor on mount and set up for new document
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      // Small delay to ensure everything is rendered
      const timer = setTimeout(() => {
        // If editor is empty, set up for new document with paragraph (natural writing)
        if (!editor.textContent.trim()) {
          editor.innerHTML = '<h1><br></h1>';
          
          // Place cursor inside the paragraph
          const p = editor.querySelector('p');
          if (p) {
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStart(p, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        
        editor.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, []); // Only run on mount

  // Handle content changes with memoization
  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Check if editor is completely empty (no text content)
    const hasContent = editor.textContent.trim().length > 0;
    
    if (!hasContent) {
      // If completely empty, reset to paragraph structure for natural writing
      editor.innerHTML = '<h1><br></h1>';
      
      // Place cursor in paragraph
      const p = editor.querySelector('p');
      if (p) {
        const range = document.createRange();
        const selection = window.getSelection();
        range.setStart(p, 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    const content = editor.innerHTML;
    onChange && onChange(content);
    updateStats();
  }, [onChange, updateStats]);

  // Handle text selection with memoization
  const handleSelect = useCallback(() => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const selectedText = selection.toString();
      const range = selection.getRangeAt(0);
      
      onSelect && onSelect({
        text: selectedText,
        start: range.startOffset,
        end: range.endOffset,
        html: range.cloneContents()
      });
    }
  }, [onSelect]);

  // Handle keyboard shortcuts with improved performance and heading indentation
  const handleKeyDown = useCallback((e) => {
    // Handle Tab key for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Insert 4 spaces for indentation
        const tabSpaces = document.createTextNode('    ');
        range.deleteContents();
        range.insertNode(tabSpaces);
        
        // Move cursor after the inserted spaces
        range.setStartAfter(tabSpaces);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      return;
    }

    // Handle Enter key for heading indentation and code blocks
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        let node = selection.anchorNode;
        
        // Check if we're in a code block first
        while (node && node !== editorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'pre') {
            // If Shift+Enter, exit the code block
            if (e.shiftKey) {
              e.preventDefault();
              
              // Create a new paragraph after the code block container
              const codeContainer = node.closest('div');
              const newParagraph = document.createElement('p');
              newParagraph.innerHTML = '<br>';
              
              // Insert after the code block container
              if (codeContainer) {
                codeContainer.parentNode.insertBefore(newParagraph, codeContainer.nextSibling);
              } else {
                node.parentNode.insertBefore(newParagraph, node.nextSibling);
              }
              
              // Move cursor to the new paragraph
              const range = document.createRange();
              range.setStart(newParagraph, 0);
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
              
              return;
            }
            // For regular Enter in code blocks, allow normal behavior (new line)
            return;
          }
          node = node.parentNode;
        }
        
        // Check if we're pressing Enter after a heading
        node = selection.anchorNode;
        while (node && node !== editorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();
            if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
              // Check for double Enter (Enter + Enter) to create new heading
              const now = Date.now();
              const lastEnter = window.lastEnterTime || 0;
              const timeDiff = now - lastEnter;
              
              console.log(`⏰ Enter pressed in ${tagName}, time diff: ${timeDiff}ms`); // Debug log
              
              if (timeDiff < 800 && timeDiff > 50) { // Double enter within 800ms but not too fast
                e.preventDefault();
                console.log(`🔄 Double Enter detected! Creating new ${tagName}`); // Debug log
                
                // Create new heading of same level
                const newHeading = document.createElement(tagName);
                newHeading.innerHTML = '<br>';
                newHeading.style.marginLeft = '0'; // Ensure left alignment
                
                // Insert after the current heading
                node.parentNode.insertBefore(newHeading, node.nextSibling);
                
                // Move cursor to the new heading
                const range = document.createRange();
                range.setStart(newHeading, 0);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                
                window.lastEnterTime = 0; // Reset
                return;
              } else {
                // Single Enter - create paragraph
                e.preventDefault();
                console.log(`📝 Single Enter in ${tagName}, creating new paragraph`); // Debug log
                
                // Create new paragraph without indentation
                const newParagraph = document.createElement('p');
                newParagraph.innerHTML = '<br>';
                newParagraph.style.marginLeft = '0'; // Ensure left alignment
                
                // Insert after the heading
                node.parentNode.insertBefore(newParagraph, node.nextSibling);
                
                // Move cursor to the new paragraph
                const range = document.createRange();
                range.setStart(newParagraph, 0);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                
                // Store timestamp for double-enter detection
                window.lastEnterTime = now;
                return;
              }
            } else if (tagName === 'p') {
              // Handle double enter in paragraphs
              const now = Date.now();
              const lastEnter = window.lastEnterTime || 0;
              const timeDiff = now - lastEnter;
              
              if (timeDiff < 800 && timeDiff > 50) { // Double enter within 800ms
                e.preventDefault();
                console.log(`🔄 Double Enter in paragraph, creating new paragraph`); // Debug log
                
                // Create new paragraph
                const newParagraph = document.createElement('p');
                newParagraph.innerHTML = '<br>';
                newParagraph.style.marginLeft = '0'; // Ensure left alignment
                
                // Insert after the current paragraph
                node.parentNode.insertBefore(newParagraph, node.nextSibling);
                
                // Move cursor to the new paragraph
                const range = document.createRange();
                range.setStart(newParagraph, 0);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                
                window.lastEnterTime = 0; // Reset
                return;
              } else {
                // Store timestamp for double-enter detection
                window.lastEnterTime = now;
              }
            }
          }
          node = node.parentNode;
        }
      }
    }

    handleInput();
  }, [handleInput]);

  // Handle paste with proper line break preservation
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    let node = selection.anchorNode;
    let isInCodeBlock = false;
    
    // Check if we're pasting into a code block
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'pre') {
        isInCodeBlock = true;
        break;
      }
      node = node.parentNode;
    }
    
    const text = e.clipboardData.getData('text/plain');
    
    if (isInCodeBlock) {
      // For code blocks, manually insert text with proper line breaks
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      // Create a text node with the content
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      
      // Move cursor to end of inserted text
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // For regular content, convert line breaks to HTML
      const lines = text.split('\n');
      const htmlContent = lines.map(line => {
        if (line.trim() === '') {
          return '<p><br></p>';
        } else {
          return `<p>${line}</p>`;
        }
      }).join('');
      
      document.execCommand('insertHTML', false, htmlContent);
    }
    
    handleInput();
  }, [handleInput]);

  // Handle toolbar commands with memoization and proper cleanup
  const handleToolbarCommand = useCallback((command, value) => {
    // Trigger input event after command execution to update content
    const timeoutId = setTimeout(() => {
      handleInput();
      // Trigger custom event for toolbar to update heading state
      if (editorRef.current) {
        const event = new CustomEvent('headingChanged');
        editorRef.current.dispatchEvent(event);
      }
    }, 0);

    // Store timeout ID for potential cleanup
    return () => clearTimeout(timeoutId);
  }, [handleInput]);

  // Add event listeners with proper cleanup using AbortController
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const abortController = new AbortController();
    const { signal } = abortController;

    // Add event listeners with abort signal for automatic cleanup
    editor.addEventListener('input', handleInput, { signal });
    editor.addEventListener('keydown', handleKeyDown, { signal });
    editor.addEventListener('keyup', handleSelect, { signal });
    editor.addEventListener('mouseup', handleSelect, { signal });
    editor.addEventListener('focus', handleSelect, { signal });
    editor.addEventListener('paste', handlePaste, { signal });

    // Cleanup function
    return () => {
      abortController.abort();
    };
  }, [handleInput, handleKeyDown, handleSelect, handlePaste]);

  // Memoize statistics components to prevent unnecessary re-renders
  const statisticsChips = useMemo(() => (
    <>
      <StatChip
        label={`${charCount} characters`}
        size="small"
        variant="outlined"
        editorTheme={theme}
      />
      <StatChip
        label={`${wordCount} words`}
        size="small"
        variant="outlined"
        editorTheme={theme}
      />
      <StatChip
        label={`${sentenceCount} sentences`}
        size="small"
        variant="outlined"
        editorTheme={theme}
      />
    </>
  ), [charCount, wordCount, sentenceCount, theme]);

  return (
    <Box
      sx={{
        backgroundColor: isDark ? '#2f3437' : 'transparent', // Transparent in light mode, let main background show
        flex: 1, // Use available space from parent
        width: '100%',
        overflow: 'auto', // Allow scrolling within this container
        position: 'relative'
      }}
      data-theme={isDark ? 'dark' : 'light'}
      data-page="docs" // Theme will target this for special docs styling
    >
      {/* Inject cursor styles */}
      <style>{cursorStyles}</style>
      
      {/* Rich Text Toolbar */}
      <RichTextToolbar 
        editorRef={editorRef} 
        theme={theme}
        isDark={isDark}
        onCommand={handleToolbarCommand}
        showAnalysisButtons={showAnalysisButtons}
        onAnalyze={onAnalyze}
        isAnalyzing={isAnalyzing}
      />

      {/* Simplified - Direct editor without unnecessary container */}
      <EditorContent
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        autoCapitalize="sentences"
        autoCorrect="on"
        spellCheck="true"
        editorTheme={theme}
        isDark={isDark}
        height={height}
        placeholder={placeholder}
        className="editor-content"
        style={{ 
          textTransform: 'none',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      />
    </Box>
  );
});

// Wrap with React.memo for performance optimization
const MemoizedRichTextEditor = React.memo(RichTextEditor);
MemoizedRichTextEditor.displayName = 'RichTextEditor';

export default MemoizedRichTextEditor;
